import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Star, LogOut, Loader2, Building2, MessageSquare, Users, Settings, FileText, TrendingUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const tenantsRes = await adminApi.getTenants();
      setTenants(tenantsRes.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const totalReviews = tenants.reduce((sum, t) => sum + (t.review_count || 0), 0);
  const activeTenants = tenants.filter(t => t.status === 'active').length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm font-medium">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm opacity-80 hover:opacity-100">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm opacity-80 hover:opacity-100">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm opacity-80 hover:opacity-100">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm opacity-80 hover:opacity-100">Gutscheine</Link>
            <Link to="/admin/meta-scores" className="text-sm opacity-80 hover:opacity-100">MetaScores</Link>
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-sm opacity-80 hidden sm:block">{user?.user?.email}</span>
            <Button variant="ghost" size="icon" onClick={logout} className="text-primary-foreground hover:bg-white/20">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container-main py-8">
        <h1 className="text-2xl font-semibold mb-8">Admin Dashboard</h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tenants</p>
                  <p className="text-3xl font-bold">{tenants.length}</p>
                </div>
                <Building2 className="w-5 h-5 text-accent" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{activeTenants} aktiv</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bewertungen</p>
                  <p className="text-3xl font-bold">{totalReviews}</p>
                </div>
                <MessageSquare className="w-5 h-5 text-accent" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Links</p>
                  <p className="text-3xl font-bold">
                    {tenants.reduce((sum, t) => sum + (t.link_count || 0), 0)}
                  </p>
                </div>
                <TrendingUp className="w-5 h-5 text-accent" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">System</p>
                  <p className="text-lg font-bold text-green-600">Online</p>
                </div>
                <Settings className="w-5 h-5 text-accent" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Link to="/admin/tenants">
            <Card className="card-shadow hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6">
                <Building2 className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1">Tenants verwalten</h3>
                <p className="text-sm text-muted-foreground">Status, Billing, Login-Reset</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/reviews">
            <Card className="card-shadow hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6">
                <MessageSquare className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1">Bewertungen</h3>
                <p className="text-sm text-muted-foreground">Alle Bewertungen einsehen</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/prompts">
            <Card className="card-shadow hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6">
                <FileText className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1">Prompts</h3>
                <p className="text-sm text-muted-foreground">KI-Prompts konfigurieren</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/meta-scores">
            <Card className="card-shadow hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6">
                <TrendingUp className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1">MetaScores</h3>
                <p className="text-sm text-muted-foreground">Externe Scores tracken</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Tenants */}
        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Tenants</CardTitle>
            <Link to="/admin/tenants">
              <Button variant="ghost" size="sm">
                Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Bewertungen</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.slice(0, 5).map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{tenant.name}</p>
                        <p className="text-xs text-muted-foreground">/{tenant.slug}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                        {tenant.status === 'active' ? 'Aktiv' : 'Deaktiviert'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {tenant.billing?.payment_status || 'trial'}
                      </Badge>
                    </TableCell>
                    <TableCell>{tenant.review_count || 0}</TableCell>
                    <TableCell>
                      <Link to={`/admin/tenants/${tenant.id}`}>
                        <Button variant="ghost" size="sm">Details</Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
